/**
 *
 *  Cymbiotika page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 13/03/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import AskConciergeModal from "../Homepage/AskConciergeModal/AskConciergeModal";
import PartnersScroller from "../Homepage/PartnersScroller/PartnersScroller";
import TrainerCard from "../Homepage/PartnersScroller/TrainerCard/TrainerCard";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils

// Icons
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Images
import cymbiotikaHero from "./hero.jpg";
import cymbiotikaHero2x from "./hero@2x.jpg";
import cymbiotikaHeroMobile from "./hero-mobile.jpg";
import cymbiotikaHeroMobile2x from "./hero-mobile@2x.jpg";
import CymbiotikaLogo from "./CymbiotikaLogo";
import Lotus from "./Lotus";
import CymbiotikaLogoSmall from "./CymbiotikaLogoSmall";

import cymbiotikaImg from "./cymbiotika-image.jpg";
import cymbiotikaImg2x from "./cymbiotika-image@2x.jpg";
import cymbiotikaImgMobile from "./cymbiotika-image-mobile.jpg";
import cymbiotikaImgMobile2x from "./cymbiotika-image-mobile@2x.jpg";

import product1 from "./products-magnesium-l-threonate.jpg";
import product12x from "./products-magnesium-l-threonate@2x.jpg";
import product2 from "./products-super-greens.jpg";
import product22x from "./products-super-greens@2x.jpg";
import product3 from "./products-longevity-mushrooms.jpg";
import product32x from "./products-longevity-mushrooms@2x.jpg";
import product4 from "./products-topical-magnesium-oil-spray.jpg";
import product42x from "./products-topical-magnesium-oil-spray@2x.jpg";
import product5 from "./products-liposomal-creatine-plus.jpg";
import product52x from "./products-liposomal-creatine-plus@2x.jpg";

import HowItWorks1 from "./HowItWorks1";
import HowItWorks2 from "./HowItWorks2";
import HowItWorks3 from "./HowItWorks3";

export default function CymbiotikaPage() {
  const { theme } = useTheme();
  const {
    user,
    products,
    productsLoaded,
    availablePartners,
    loadProducts,
    loadAvailablePartners,
    clearAvailablePartnersLoaded,
  } = useAppState();

  const heroRef = React.useRef(null);
  const [isAskConciergeModalOpen, setIsAskConciergeModalOpen] = useState(false);

  const isUserLoggedIn = !!(user && user.id);

  React.useEffect(() => {
    let params = {};

    if (!products || !products.length) {
      return;
    }

    const cymbiotikaProduct = products.find((val) =>
      val.name.toLowerCase().includes("cymbiotika")
    );

    if (cymbiotikaProduct) {
      params.productId = cymbiotikaProduct.id;
    }

    loadAvailablePartners(params, user ? user.region : "US");
  }, [user, products]);

  React.useEffect(() => {
    if (!productsLoaded) {
      loadProducts(user ? user.region : "US");
    }

    // return () => clearAvailablePartnersLoaded();
  }, [user]);

  return (
    <>
      <ScrollToTopOnMount />
      <Meta
        name="description"
        content={`On-demand, in person, personal training in Los Angeles.`}
      />
      <Header behaviour="none" isLoggedIn={isUserLoggedIn} heroRef={heroRef} />

      <div
        ref={heroRef}
        className="tbk-pt-7_ tbk-relative tbk-flex tbk-min-h-[528px] tbk-flex-col-reverse tbk-overflow-hidden tbk-bg-[#6C8856] md:tbk-min-h-[auto] lg:tbk-flex-row"
      >
        <div className="tbk-w-full_ tbk-right-0 tbk-right-0 tbk-bottom-0 tbk-top-0 tbk-left-1/2 tbk-flex tbk-items-end tbk-overflow-hidden lg:tbk-absolute">
          <div className="tbk-h-full tbk-w-full">
            <picture className="tbk-h-full tbk-grow">
              <source
                media="(min-width: 768px)"
                srcSet={`${cymbiotikaHero} 1x, ${cymbiotikaHero2x} 2x, ${cymbiotikaHero2x} 3x`}
              />
              <source
                srcSet={`${cymbiotikaHeroMobile} 1x, ${cymbiotikaHeroMobile2x} 2x, ${cymbiotikaHeroMobile2x} 3x`}
              />

              <img
                className="tbk-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
                src={cymbiotikaHero}
                alt="Hero"
              />
            </picture>
          </div>
        </div>
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="tbk-relative tbk-h-full md:tbk-grid md:tbk-grid-cols-2 md:tbk-gap-8">
            <div className={"tbk-pt-4 tbk-pb-4  lg:tbk-pt-7 lg:tbk-pb-34"}>
              <div className={"tbk-mb-4 tbk-flex"}>
                <CymbiotikaLogo className="tbk-mr-2" />
                <Lotus />
              </div>
              <h1 className="tbk-text-epic-medium md:tbk-text-h2-black tbk-mb-1 tbk-block !tbk-leading-none tbk-text-basic-white tbk-text-basic-white lg:tbk-mb-1 lg:!tbk-leading-slight">
                Cymbiotika Coach Program
              </h1>
              <h2 className={"tbk-text-bright tbk-text-basic-white"}>
                powered by TruBe
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-pt-4 tbk-pb-4 lg:tbk-pt-8">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-4 lg:tbk-mb-0">
              <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                Introducing Cymbiotika Certified, a coach program designed to
                elevate your health and well-being. By combining carefully
                selected Cymbiotika products with personalized training sessions
                led by TruBe experts, we're offering a comprehensive approach to
                optimizing your daily health routine. Our coaches will identify
                any nutritional gaps, recommend the perfect Cymbiotika product,
                and provide tailored training sessions, empowering you on your
                journey to a healthier you. Let us support you every step of the
                way.
              </p>
              <Link
                to={
                  isUserLoggedIn || true
                    ? "/partners/cymbiotika-certified"
                    : "/become-a-member"
                }
              >
                <Button
                  size="md"
                  variant="secondary"
                  className="tbk-w-44"
                  endIcon={<ArrowRightIcon />}
                >
                  {isUserLoggedIn || true ? "Try It Out" : "Become a Member"}
                </Button>
              </Link>
            </div>
            <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl tbk-bg-home-our-coaches-mobile lg:tbk-h-auto lg:tbk-bg-none">
              <picture>
                <source
                  media="(min-width: 768px)"
                  srcSet={`${cymbiotikaImg} 1x, ${cymbiotikaImg2x} 2x, ${cymbiotikaImg2x} 3x`}
                />
                <source
                  srcSet={`${cymbiotikaImgMobile} 1x, ${cymbiotikaImgMobile2x} 2x`}
                />
                <img
                  className="tbk-relative tbk-h-full tbk-w-full tbk-object-cover"
                  role="presentation"
                  src={`${cymbiotikaImg}`}
                  alt="Cymbiotika"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>

      {availablePartners && availablePartners.length ? (
        <div className="tbk-relative tbk-bg-basic-white tbk-pt-7 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-0">
          <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 lg:tbk-pb-5">
            <div className="tbk-mb-2 tbk-flex tbk-justify-center">
              <CymbiotikaLogoSmall />
            </div>
            <div className="tbk-relative tbk-mb-5 tbk-text-center">
              <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
                Cymbiotika Certified Coaches
              </h3>
              <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
            </div>

            <div className="tbk--mx-2 tbk-mb-4 lg:tbk-mb-6">
              {availablePartners && availablePartners.length ? (
                <PartnersScroller
                  partners={availablePartners}
                  renderPartner={({
                    info: partnerInfo,
                    index,
                    contentScrollerApi,
                    onCardVisibleChange,
                  }) => (
                    <Link to={`/partners/${partnerInfo.id}`}>
                      <TrainerCard
                        trainer={partnerInfo}
                        key={partnerInfo.id}
                        itemKey={index}
                        contentScrollerApi={contentScrollerApi}
                        onCardVisibleChange={onCardVisibleChange}
                        // onCardClick={handleCardClick}
                      />
                    </Link>
                  )}
                />
              ) : null}
            </div>
            <p className="tbk-text-main tbk-mb-4 tbk-px-2 tbk-text-center tbk-text-primary lg:tbk-hidden">
              Each coach was meticulously selected and underwent a rigorous
              onboarding and training process. Their coaching approach is
              designed not only to help you achieve your fitness goals but also
              reach your optimal health levels by infusing personalized
              Cymbiotika products into your daily routine.
            </p>
            <Link
              className="tbk-block tbk-text-center"
              to={
                isUserLoggedIn || true
                  ? "/partners/cymbiotika-certified"
                  : "/become-a-member"
              }
            >
              <Button
                variant="secondary"
                className="tbk-inline-flex tbk-w-66 tbk-pl-5 tbk-pr-5"
                endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
              >
                View The Coaches
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-7 lg:tbk-pt-4 lg:tbk-pb-9">
        <div className="tbk-container tbk-mx-auto tbk-px-3 lg:tbk-px-8">
          <div className="tbk-relative tbk-mt-0 tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-blue-grey">
              How it works
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
          <div className="tbk-mb-7 md:tbk-grid md:tbk-grid-cols-2 md:tbk-gap-5 lg:tbk-grid-cols-3 lg:tbk-pt-3">
            <div className="tbk-mb-5 tbk-rounded-2xl tbk-border tbk-border-blue-grey-light lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <div className="tbk-h-58" />
                <HowItWorks1 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-[#6C8856]">
                  Access
                </div>
              </div>
              <div className="tbk-text-main lg:tbk-text-h3-subtitle tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary lg:tbk-text-left">
                Access Your Cymbiotika coach in your area by inputting your
                desired in-person session location
              </div>
            </div>
            <div className="tbk-mb-5 tbk-rounded-2xl tbk-border tbk-border-blue-grey-light lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <div className="tbk-h-58" />
                <HowItWorks2 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-[#6C8856]">
                  Connect
                </div>
              </div>
              <div className="tbk-text-main lg:tbk-text-h3-subtitle tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary lg:tbk-text-left">
                Book your coach and connect before the session via text message
                to personalize your in-person session and determine the right
                Cymbiotika product fit for your goals.
              </div>
            </div>

            <div className="tbk-mb-5 tbk-rounded-2xl tbk-border tbk-border-blue-grey-light lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <div className="tbk-h-58" />
                <HowItWorks3 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-[#6C8856]">
                  Train
                </div>
              </div>
              <div className="tbk-text-main lg:tbk-text-h3-subtitle tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary lg:tbk-text-left">
                Train 1:1 with continuous feedback and analysis to your goals
                and product results.
              </div>
            </div>
          </div>
          <Link
            className="tbk-block tbk-text-center"
            to={
              isUserLoggedIn || true
                ? "/partners/cymbiotika-certified"
                : "/become-a-member"
            }
          >
            <Button
              variant="secondary"
              className="tbk-inline-flex tbk-w-66 tbk-pl-5 tbk-pr-5"
              endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
            >
              Start now
            </Button>
          </Link>
        </div>
      </div>

      <div className="tbk-overflow-hidden tbk-pt-7 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-30">
        <div className="tbk-container tbk-mx-auto tbk-px-3 lg:tbk-px-8">
          <div className="tbk-mt-5_ tbk-relative tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              Cymbiotika Products
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>

          <div className="tb-slider tb-cymbiotika-products-slider tbk-grid-cols-5 tbk-flex-nowrap tbk-gap-3 xl:tbk-grid">
            <div id={`tb-slide-${1}`} className="tb-slide">
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <a
                  href="https://cymbiotika.com/collections/supplements-shop-all/products/magnesium-l-threonate"
                  target="_blank"
                >
                  <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                    <img
                      className="tbk-h-57 tbk-w-full tbk-min-w-[298px] lg:tbk-min-w-full"
                      src={product1}
                      srcSet={`${product12x} 2x,`}
                      alt="Product 1"
                    />
                  </div>
                </a>
                <div className="tbk-text-h3-subtitle tbk-mt-2 tbk-mb-1 tbk-text-primary">
                  Magnesium L-Threonate
                </div>
                <div className="tbk-text-small tbk-text-basic-trueblack">
                  <ul className="tbk-list-disc">
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Boost brain power
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Balance mood and calm central nervous system
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Improve memory
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div id={`tb-slide-${2}`} className="tb-slide">
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <a
                  href="https://cymbiotika.com/collections/supplements-shop-all/products/supergreens"
                  target="_blank"
                >
                  <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                    <img
                      className="tbk-h-57 tbk-w-full tbk-min-w-[298px] lg:tbk-min-w-full"
                      src={product2}
                      srcSet={`${product22x} 2x,`}
                      alt="Product 2"
                    />
                  </div>
                </a>
                <div className="tbk-text-h3-subtitle tbk-mt-2 tbk-mb-1 tbk-text-primary">
                  Super Greens
                </div>
                <div className="tbk-text-small tbk-text-basic-trueblack">
                  <ul className="tbk-list-disc">
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Supports cellular detoxification
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Boosts energy levels
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Promotes healthy immune response
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div id={`tb-slide-${3}`} className="tb-slide">
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <a
                  href="https://cymbiotika.com/collections/supplements-shop-all/products/organic-longevity-mushrooms"
                  target="_blank"
                >
                  <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                    <img
                      className="tbk-h-57 tbk-w-full tbk-min-w-[298px] lg:tbk-min-w-full"
                      src={product3}
                      srcSet={`${product32x} 2x,`}
                      alt="Product 3"
                    />
                  </div>
                </a>
                <div className="tbk-text-h3-subtitle tbk-mt-2 tbk-mb-1 tbk-text-primary">
                  Longevity Mushrooms
                </div>
                <div className="tbk-text-small tbk-text-basic-trueblack">
                  <ul className="tbk-list-disc">
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Clinically proven to enhance athletic performance
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Improves exercise recovery time
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Supports a healthy immune system and central nervous
                      systems
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div id={`tb-slide-${4}`} className="tb-slide">
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <a
                  href="https://cymbiotika.com/collections/supplements-shop-all/products/magnesium-oil"
                  target="_blank"
                >
                  <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                    <img
                      className="tbk-h-57 tbk-w-full tbk-min-w-[298px] lg:tbk-min-w-full"
                      src={product4}
                      srcSet={`${product42x} 2x,`}
                      alt="Product 4"
                    />
                  </div>
                </a>
                <div className="tbk-text-h3-subtitle tbk-mt-2 tbk-mb-1 tbk-text-primary">
                  Topical Magnesium Oil Spray
                </div>
                <div className="tbk-text-small tbk-text-basic-trueblack">
                  <ul className="tbk-list-disc">
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Supports joint and muscular health
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Promotes relaxation and decreases symptoms of anxiety
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Reduces inflammation
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div id={`tb-slide-${5}`} className="tb-slide">
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <a
                  href="https://cymbiotika.com/collections/supplements-shop-all/products/creatine"
                  target="_blank"
                >
                  <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                    <img
                      className="tbk-h-57 tbk-w-full tbk-min-w-[298px] lg:tbk-min-w-full"
                      src={product5}
                      srcSet={`${product52x} 2x,`}
                      alt="Product 4"
                    />
                  </div>
                </a>
                <div className="tbk-text-h3-subtitle tbk-mt-2 tbk-mb-1 tbk-text-primary">
                  Liposomal Creatine+
                </div>
                <div className="tbk-text-small tbk-text-basic-trueblack">
                  <ul className="tbk-list-disc">
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Boost athletic performance, strength, and recovery time
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Support brain health and cognition
                    </li>
                    <li className="tbk-ml-3 tbk-shrink-0 tbk-grow">
                      Promote youthful skin
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <AskConciergeModal
        isOpen={isAskConciergeModalOpen}
        onClose={() => setIsAskConciergeModalOpen(false)}
      />
    </>
  );
}
